import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../styles/components/resumeTailoring/resumeComponent.scss";
import "react-circular-progressbar/dist/styles.css";
import axiosClient from "../../axios-client";
import Resume from "./Resume";
import Suggestions from "./Suggestions";
import { AIProvider } from "./AIContext";
import axios from "axios";
import { mainUrl } from "../../constant";
import ResumeRealityCheckerV2 from "./resume_scoring";

export interface Education {
  degree: string;
  field: string;
  university: string;
  graduationDate: string; // Match the backend
  gpa?: string;
}

export interface Experience {
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string | null;
  isCurrentPosition: boolean;
  description: { id: string; text: string }[];
}

export interface Project {
  title: string;
  project_name: string;
  startDate: string;
  endDate: string | null;
  isOngoing: boolean;
  description: { id: string; text: string }[];
}

export interface ResumeText {
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  linkedin: string;
  website: string;
  location: string;
  citizenship: string;
  education: Education[];
  experience: Experience[];
  projects: Project[];
  skills: any;
  interests: string[];
}

interface ResumeComponentProps {
  jobDescription: any;
  resumeText: ResumeText;
  handleTailorButtonClick: () => void;
  resumeId: string;
  jobId: string;
  onNavigateToCoverLetter: () => void;
  hasBeenTailored: boolean;
  setHasBeenTailored: (value: boolean) => void;
  cachedContent: { suggestions: any[]; score: number | null } | null;
  setCachedContent: (
    content: { suggestions: any[]; score: number | null } | null
  ) => void;
}

export interface BulletPointSuggestion {
  id: string;
  original: string;
  suggested: string;
  isLoading: boolean;
  expIndex: number;
  descId: string;
}

const ResumeComponent: React.FC<ResumeComponentProps> = ({
  jobDescription,
  resumeText,
  handleTailorButtonClick,
  resumeId,
  jobId,
  onNavigateToCoverLetter,
  hasBeenTailored,
  setHasBeenTailored,
  cachedContent,
  setCachedContent,
}) => {
  const [revisedResume, setRevisedResume] = useState<ResumeText>(resumeText);
  const [suggestions, setSuggestions] = useState<BulletPointSuggestion[]>([]);
  const [score, setScore] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);
  const [selectedBulletPoint, setSelectedBulletPoint] =
    useState<BulletPointSuggestion | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // New AI scoring engine state
  const [scoringEngine, setScoringEngine] = useState<any>(null);
  const [isScoreLoading, setIsScoreLoading] = useState<boolean>(false);

  // 🎯 SMART SESSION MANAGEMENT - Track session state
  const [sessionActive, setSessionActive] = useState(true);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [expandedBulletPoint, setExpandedBulletPoint] = useState<{
    expIndex: number;
    descId: string;
  } | null>(null);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // Initialize AI scoring engine with proper cleanup
  useEffect(() => {
    let isMounted = true;
    let currentEngine: any = null;

    const initializeScoringEngine = async () => {
      try {
        const scorer = new ResumeRealityCheckerV2();
        currentEngine = scorer;
        
        // Only set state if component is still mounted
        if (isMounted) {
          setScoringEngine(scorer);
        } else {
          // Component unmounted during init - cleanup immediately
          if (scorer && scorer.dispose) {
            scorer.dispose();
          }
        }
      } catch (error) {
        console.error('Failed to initialize scoring engine:', error);
        if (isMounted) {
          setScoringEngine(null);
        }
      }
    };

    initializeScoringEngine();

    // Cleanup function with access to current engine reference
    return () => {
      isMounted = false;
      if (currentEngine && currentEngine.dispose) {
        currentEngine.dispose();
        currentEngine = null;
      }
    };
  }, []);

  // Convert resume to text for analysis
  const convertResumeToText = useCallback((resume: ResumeText): string => {
    const sections = [];
    
    // Contact info
    if (resume.firstName || resume.lastName) {
      sections.push(`${resume.firstName} ${resume.lastName}`);
    }
    if (resume.email) sections.push(`Email: ${resume.email}`);
    if (resume.phone) sections.push(`Phone: ${resume.phone}`);
    if (resume.location) sections.push(`Location: ${resume.location}`);
    
    // Experience
    if (resume.experience?.length) {
      sections.push('\nEXPERIENCE:');
      resume.experience.forEach(exp => {
        sections.push(`${exp.title} | ${exp.company} (${exp.startDate} - ${exp.endDate || 'Present'})`);
        exp.description.forEach(desc => {
          sections.push(`• ${desc.text}`);
        });
      });
    }
    
    // Projects
    if (resume.projects?.length) {
      sections.push('\nPROJECTS:');
      resume.projects.forEach(project => {
        sections.push(`${project.title} (${project.startDate} - ${project.endDate || 'Present'})`);
        project.description.forEach(desc => {
          sections.push(`• ${desc.text}`);
        });
      });
    }
    
    // Education
    if (resume.education?.length) {
      sections.push('\nEDUCATION:');
      resume.education.forEach(edu => {
        sections.push(`${edu.degree} in ${edu.field} | ${edu.university} (${edu.graduationDate})`);
      });
    }
    
    // Skills
    if (resume.skills && Object.keys(resume.skills).length) {
      sections.push('\nSKILLS:');
      Object.entries(resume.skills).forEach(([category, skillList]: [string, any]) => {
        if (Array.isArray(skillList) && skillList.length) {
          sections.push(`${category}: ${skillList.join(', ')}`);
        }
      });
    }
    
    return sections.join('\n');
  }, []);

  // Memoized resume text conversion for better performance
  const memoizedResumeText = useMemo(() => {
    return convertResumeToText(revisedResume);
  }, [revisedResume, convertResumeToText]);

  // Memoized job description processing
  const memoizedJobDescription = useMemo(() => {
    if (typeof jobDescription === 'string') {
      return jobDescription;
    } else if (jobDescription && typeof jobDescription === 'object') {
      // If jobDescription is the job object, construct a comprehensive description
      const jobParts = [];
      
      // Add job title and company
      if (jobDescription.title) jobParts.push(`Job Title: ${jobDescription.title}`);
      if (jobDescription.company) jobParts.push(`Company: ${jobDescription.company}`);
      
      // Add summary/description
      if (jobDescription.summary) jobParts.push(`About the Job: ${jobDescription.summary}`);
      if (jobDescription.company_description) jobParts.push(`About the Company: ${jobDescription.company_description}`);
      
      // Add qualifications
      if (jobDescription.qualifications && jobDescription.qualifications.length > 0) {
        jobParts.push(`Requirements: ${jobDescription.qualifications.join('; ')}`);
      }
      
      // Add responsibilities
      if (jobDescription.responsibilities && jobDescription.responsibilities.length > 0) {
        jobParts.push(`Responsibilities: ${jobDescription.responsibilities.join('; ')}`);
      }
      
      // Add skills
      if (jobDescription.skills) {
        const skillsText = Array.isArray(jobDescription.skills) 
          ? jobDescription.skills.join(', ') 
          : jobDescription.skills;
        jobParts.push(`Skills: ${skillsText}`);
      }
      
      // Add other relevant info
      if (jobDescription.experience_level) jobParts.push(`Experience Level: ${jobDescription.experience_level}`);
      if (jobDescription.work_environment) jobParts.push(`Work Environment: ${jobDescription.work_environment}`);
      
      return jobParts.join('\n\n');
    } else {
      // Fallback for empty or invalid job description
      return 'No job description available';
    }
  }, [jobDescription]);

  // Immediate scoring function for instant results with memoization
  const immediateAnalysis = useCallback(
    async (resume: ResumeText, jobDesc: any) => {
      if (!scoringEngine || !resume || !jobDesc) return;

      setIsScoreLoading(true);
      try {
        const result = await scoringEngine.checkHearingBackLikelihood({
          resumeText: memoizedResumeText,
          jobDescription: memoizedJobDescription
        });

        setScore(result.score);
        
        // Update cached content when score changes
        if (cachedContent) {
          setCachedContent({ ...cachedContent, score: result.score });
        }
      } catch (error) {
        console.error('Reality check failed:', error);
        setScore(null);
        
        // Update cached content when score fails
        if (cachedContent) {
          setCachedContent({ ...cachedContent, score: null });
        }
      } finally {
        setIsScoreLoading(false);
      }
    },
    [scoringEngine, memoizedResumeText, memoizedJobDescription]
  );

  // Debounce helper function with proper cleanup
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const debounce = useCallback((func: (...args: any[]) => void, wait: number) => {
    return (...args: any[]) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => func(...args), wait);
    };
  }, []);

  // Cleanup on unmount - industry standard
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Reset fetch state for next mount
      fetchInProgressRef.current = false;
      hasAttemptedFetchRef.current = false;
    };
  }, []);

  // Debounced analysis function for content changes (much faster now)
  const debouncedAnalysis = useCallback(
    debounce(async (resume: ResumeText, jobDesc: any) => {
      await immediateAnalysis(resume, jobDesc);
    }, 100), // Reduced to 100ms for near-instant updates
    [immediateAnalysis, debounce]
  );

  // One-shot state management - industry standard
  const fetchInProgressRef = useRef(false);
  const hasAttemptedFetchRef = useRef(false);

  const fetchSuggestions = React.useCallback(async () => {
    // 🚫 ABSOLUTE PREVENTION of duplicate calls
    if (hasBeenTailored || fetchInProgressRef.current || hasAttemptedFetchRef.current) {
      return;
    }

    // 🚫 EXTRA SAFEGUARD - Check if we already have suggestions
    if (suggestions.length > 0) {
      return;
    }

    hasAttemptedFetchRef.current = true;
    fetchInProgressRef.current = true;

    const user = JSON.parse(String(localStorage.getItem("user")));
    setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      // Better job description extraction - handle the job object properly
      let description = '';
      
      if (typeof jobDescription === 'string') {
        description = jobDescription;
      } else if (jobDescription && typeof jobDescription === 'object') {
        // If jobDescription is the job object, construct a comprehensive description
        const jobParts = [];
        
        // Add job title and company
        if (jobDescription.title) jobParts.push(`Job Title: ${jobDescription.title}`);
        if (jobDescription.company) jobParts.push(`Company: ${jobDescription.company}`);
        
        // Add summary/description
        if (jobDescription.summary) jobParts.push(`About the Job: ${jobDescription.summary}`);
        if (jobDescription.company_description) jobParts.push(`About the Company: ${jobDescription.company_description}`);
        
        // Add qualifications
        if (jobDescription.qualifications && jobDescription.qualifications.length > 0) {
          jobParts.push(`Requirements: ${jobDescription.qualifications.join('; ')}`);
        }
        
        // Add responsibilities
        if (jobDescription.responsibilities && jobDescription.responsibilities.length > 0) {
          jobParts.push(`Responsibilities: ${jobDescription.responsibilities.join('; ')}`);
        }
        
        // Add skills
        if (jobDescription.skills) {
          const skillsText = Array.isArray(jobDescription.skills) 
            ? jobDescription.skills.join(', ') 
            : jobDescription.skills;
          jobParts.push(`Skills: ${skillsText}`);
        }
        
        // Add other relevant info
        if (jobDescription.experience_level) jobParts.push(`Experience Level: ${jobDescription.experience_level}`);
        if (jobDescription.work_environment) jobParts.push(`Work Environment: ${jobDescription.work_environment}`);
        
        description = jobParts.join('\n\n');
      } else {
        // Fallback - shouldn't reach here but just in case
        description = JSON.stringify(jobDescription);
      }

      // Final validation - ensure we have actual content
      if (!description || description.trim().length < 10) {
        throw new Error('Job description is too short or missing. Please wait for the job details to load completely.');
      }

      console.log('🔍 Sending job description to backend:', description.substring(0, 200) + '...');
      
      // === Streaming call – we collect chunks then parse once ===
      const baseUrl = axiosClient.defaults.baseURL || '/v1';
      const response = await fetch(`${baseUrl}/builder/revolutionary-resume-tailoring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          jobDescription: description,
          userDetails: JSON.stringify(user),
        }),
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let rawResponse = '';

      if (reader) {
        while (true) {
          const { value, done } = await reader.read();
          if (value) rawResponse += decoder.decode(value, { stream: true });
          if (done) {
            // flush remaining bytes
            rawResponse += decoder.decode();
            break;
          }
        }
      }

      // Handle the structured response from the accumulated string
      let parsedData;

      try {
        // First try direct JSON parse of the streamed string
        parsedData = JSON.parse(rawResponse);
      } catch (e) {
        // Fallback: maybe the backend wrapped JSON in a markdown code block
        const match = rawResponse.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
        if (match && match[1]) {
          parsedData = JSON.parse(match[1].trim());
        } else {
          console.error('JSON parsing failed:', e);
          console.error('Raw response (first 500 chars):', rawResponse.substring(0, 500));
          throw new Error('Unable to parse backend response. Please try again.');
        }
      }

      // If backend used the { success: true, data: {…} } envelope, unwrap it
      if (parsedData && parsedData.success && parsedData.data) {
        parsedData = parsedData.data;
      }

      // Validate response structure - simple and fast
      if (!parsedData?.bullet_points || !Array.isArray(parsedData.bullet_points) || parsedData.bullet_points.length === 0) {
        throw new Error('No suggestions were generated. Please try again.');
      }

      // Simple UUID-based mapping - backend should return same UUIDs as existing bullet points
      const findBulletPointByUUID = (bulletId: string) => {
        // Search through all experiences to find matching bullet point by UUID
        for (let expIndex = 0; expIndex < revisedResume.experience.length; expIndex++) {
          const experience = revisedResume.experience[expIndex];
          for (const desc of experience.description) {
            if (desc.id === bulletId) {
              return { expIndex, descId: desc.id, section: 'experience' };
            }
          }
        }
        
        // Search through projects to find matching bullet point by UUID
        for (let projIndex = 0; projIndex < revisedResume.projects.length; projIndex++) {
          const project = revisedResume.projects[projIndex];
          for (const desc of project.description) {
            if (desc.id === bulletId) {
              return { expIndex: projIndex, descId: desc.id, section: 'project' };
            }
          }
        }
        
        return { expIndex: 0, descId: bulletId, section: 'unknown' };
      };

      const transformedSuggestions = parsedData.bullet_points.map(
        (item: any) => {
          const mapping = findBulletPointByUUID(item.id);
          return {
            id: item.id,
            original: item.original || item.text,
            suggested: item.text,
            isLoading: false,
            expIndex: mapping.expIndex,
            descId: mapping.descId,
          };
        }
      );

      setSuggestions(transformedSuggestions);
      setHasBeenTailored(true);
      
      // Cache the suggestions for tab switching
      setCachedContent({
        suggestions: transformedSuggestions,
        score: score, // Preserve current score
      });
      
    } catch (err) {
      console.error("Fetch suggestions failed:", err);
      setError(err instanceof Error ? err.message : "Failed to generate suggestions. Please try again.");
      // Reset attempt flag on error so user can retry
      hasAttemptedFetchRef.current = false;
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, []);  // Empty deps - fetchSuggestions is stable, only call when explicitly needed

  // 🎯 UNIFIED CACHE & FETCH LOGIC - Prevents duplicate API calls
  useEffect(() => {
    // First priority: Check if we have cached suggestions
    if (cachedContent && cachedContent.suggestions && cachedContent.suggestions.length > 0) {
      // Restore cached suggestions
      setSuggestions(cachedContent.suggestions);
      if (cachedContent.score !== null) {
        setScore(cachedContent.score);
      }
      setLoading(false);
      setError(null);
      setHasBeenTailored(true);
      return;
    }

    // Second priority: Fetch new suggestions if needed
    if (!hasBeenTailored && jobDescription && memoizedJobDescription !== 'No job description available') {
      fetchSuggestions();
    } else {
      setLoading(false);
    }
  }, [hasBeenTailored, cachedContent, jobDescription, memoizedJobDescription, setHasBeenTailored]); // Single unified effect

  // Immediate scoring when modal opens or scoring engine loads
  useEffect(() => {
    if (scoringEngine && jobDescription && revisedResume && memoizedJobDescription !== 'No job description available') {
      immediateAnalysis(revisedResume, jobDescription);
    }
  }, [scoringEngine, jobDescription, immediateAnalysis, memoizedJobDescription]); // Remove revisedResume to only trigger on modal open

  // Real-time scoring when resume content changes (debounced for smooth editing)
  useEffect(() => {
    if (scoringEngine && jobDescription && revisedResume && memoizedJobDescription !== 'No job description available') {
      debouncedAnalysis(revisedResume, jobDescription);
    }
  }, [revisedResume, debouncedAnalysis, jobDescription, memoizedJobDescription]); // Only trigger on resume changes

  // 💥 CLEANUP - Only reset fetch flags on unmount, preserve cache for tab switching
  useEffect(() => {
    return () => {
      // Only reset component-specific state, NOT the parent's cached content or hasBeenTailored
      setSessionActive(false);
      
      // Reset only internal fetch flags for next mount
      fetchInProgressRef.current = false;
      hasAttemptedFetchRef.current = false;
      
      // DON'T clear cached content or reset hasBeenTailored - let parent handle that
    };
  }, []); // Empty deps = runs only on unmount

  const handleToggleDropdown = (id: string) => {
    setExpandedIndex(expandedIndex === id ? null : id);
  };

  const handleFieldChange = (updater: (prev: ResumeText) => ResumeText) => {
    const updatedResume = updater(revisedResume);
      setRevisedResume(updatedResume);

      // Get all content from resume-rectangle container
      const allContent = {
        // Contact information
        ...(updatedResume.firstName && { firstName: updatedResume.firstName }),
        ...(updatedResume.lastName && { lastName: updatedResume.lastName }),
        ...(updatedResume.email && { email: updatedResume.email }),
        ...(updatedResume.phone && { phone: updatedResume.phone }),
        ...(updatedResume.linkedin && { linkedin: updatedResume.linkedin }),
        ...(updatedResume.website && { website: updatedResume.website }),
        ...(updatedResume.location && { location: updatedResume.location }),
        ...(updatedResume.citizenship && { citizenship: updatedResume.citizenship }),
        // Arrays and objects - only include if they exist and have content
        ...(updatedResume.education?.length > 0 && { education: updatedResume.education }),
        ...(updatedResume.experience?.length > 0 && { experience: updatedResume.experience }),
        ...(updatedResume.projects?.length > 0 && { projects: updatedResume.projects }),
        ...(Object.keys(updatedResume.skills || {}).length > 0 && { skills: updatedResume.skills }),
        ...(updatedResume.interests?.length > 0 && { interests: updatedResume.interests })
      };

              // localStorage removed for performance - data stays in memory
      
      // Resume content updated
  };

  const handleAcceptSingleSuggestion = async (
    suggestion: string,
    suggestionId: string
  ) => {
    try {
      let found = false;

      const updatedExperience = revisedResume.experience.map((experience) => {
        const updatedDescriptions = experience.description.map((bullet) => {
          if (bullet.id === suggestionId && !found) {
            found = true;
            return { ...bullet, text: suggestion };
          }
          return bullet;
        });
        return { ...experience, description: updatedDescriptions };
      });

      const updatedProjects = revisedResume.projects.map((project) => {
        const updatedDescriptions = project.description.map((bullet) => {
          if (bullet.id === suggestionId && !found) {
            found = true;
            return { ...bullet, text: suggestion };
          }
          return bullet;
        });
        return { ...project, description: updatedDescriptions };
      });

      if (found) {
        const updatedResume = {
          ...revisedResume,
          experience: updatedExperience,
          projects: updatedProjects
        };

        setRevisedResume(updatedResume);
        await saveResume(updatedResume);

        // localStorage removed for performance - resume updates stay in memory

        const updatedSuggestions = suggestions.filter(
          (item) => item.id !== suggestionId
        );
        setSuggestions(updatedSuggestions);
        
        // Update cached content when suggestions change
        setCachedContent({
          suggestions: updatedSuggestions,
          score: score,
        });

        setExpandedBulletPoint(null);
      }
    } catch (error: unknown) {
      console.error("Error accepting suggestion:", error);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axiosClient.post(
        "/pdf/generate-pdf",
        revisedResume
      );

      if (response.data && response.data.downloadUrl) {
        // Download without opening a new tab: fetch the PDF as a blob then trigger a hidden anchor click.
        const pdfBlobResponse = await axios.get(
          `${mainUrl}${response.data.downloadUrl}`,
          { responseType: 'blob' }
        );

        const blob = new Blob([pdfBlobResponse.data], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${revisedResume.firstName}_${revisedResume.lastName}_resume.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoke the object URL after a short delay to free memory
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        throw new Error('Download URL not received from server');
      }
    } catch (error: unknown) {
      console.error("Error generating PDF:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error response:", error.response.data);
      }
    }
  };

  const saveResume = async (resumeToSave = revisedResume as any) => {
    try {
      if (!resumeId) {
        // No resumeId found, skipping save
        return;
      }

      const user = JSON.parse(String(localStorage.getItem("user")));
      resumeToSave.userId = user._id;
      resumeToSave.jobId = jobId;

      const response = await axiosClient.put(
        `/builder/update-resume/${resumeId}`,
        resumeToSave
      );
      // Resume updated successfully
    } catch (error: unknown) {
      console.error("Error saving resume:", error);
    }
  };

  const handleTailorBulletPoint = async (expIndex: number, descId: string) => {
    setExpandedBulletPoint({ expIndex, descId });
    try {
      const bulletPoints: string[] = [];

      revisedResume.experience.forEach((exp) => {
        exp.description.forEach((desc) => {
          if (desc.id === descId) {
            bulletPoints.push(desc.text);
          }
        });
      });

      if (bulletPoints.length === 0) {
        revisedResume.projects.forEach((project) => {
          project.description.forEach((desc) => {
            if (desc.id === descId) {
              bulletPoints.push(desc.text);
            }
          });
        });
      }

      // Use the same job description extraction logic as fetchSuggestions
      const description = memoizedJobDescription;

      const response = await axiosClient.post("builder/intelligent-bullet-tailoring", {
        bulletPoint: bulletPoints[0],
        jobDescription: description,
      });

      setSelectedBulletPoint({
        id: descId,
        original: bulletPoints[0],
        suggested: response.data.success ? response.data.data : response.data,
        isLoading: false,
        expIndex,
        descId,
      });
    } catch (err) {
      setError(
        "Failed to fetch bullet point suggestion. Please try again later."
      );
      setSelectedBulletPoint(null);
    } finally {
      // No longer need to unlock editing since it's always enabled
    }
  };

  const handleAcceptSuggestion = async (expIndex: number, descId: string) => {
    if (selectedBulletPoint && !selectedBulletPoint.isLoading) {
      handleFieldChange((prev) => {
        const updatedExperience = [...prev.experience];
        const bulletIndex = updatedExperience[expIndex].description.findIndex(
          (bullet) => bullet.id === descId
        );

        if (bulletIndex !== -1) {
          updatedExperience[expIndex].description[bulletIndex].text =
            selectedBulletPoint.suggested;
        }

        return { ...prev, experience: updatedExperience };
      });
      setSelectedBulletPoint(null);
    }
  };

  const handleRejectSuggestion = () => {
    setSelectedBulletPoint(null);
  };

  // Only keep the sidebar responsiveness effect
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1200) {
        setIsSidebarCollapsed(true);
        setIsSidebarExpanded(false);
      } else {
        setIsSidebarCollapsed(false);
        setIsSidebarExpanded(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isSidebarExpanded &&
        !target.closest(".expanded-sidebar") &&
        !target.closest(".collapsed-sidebar")
      ) {
        setIsSidebarExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarExpanded]);

  const handleCoverLetterClick = async () => {
    // localStorage removed for performance - navigation handled without browser storage
    await handleTailorButtonClick();
  };

  return (
    <AIProvider>
      <>
        <div className="resume-layout-wrapper">
          <div className="left-container">
            <Resume
              revisedResume={revisedResume}
              handleFieldChange={handleFieldChange}
              handleTailorBulletPoint={handleTailorBulletPoint}
              selectedBulletPoint={selectedBulletPoint}
              handleAcceptSuggestion={handleAcceptSuggestion}
              handleRejectSuggestion={handleRejectSuggestion}
              expandedBulletPoint={expandedBulletPoint}
            />
          </div>

          {!isSidebarCollapsed && (
            <div className="right-container">
              <Suggestions
                suggestions={suggestions}
                expandedIndex={expandedIndex}
                handleToggleDropdown={handleToggleDropdown}
                isHovered={isHovered}
                handleTailorButtonClick={handleCoverLetterClick}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                handleSingleSuggestion={handleAcceptSingleSuggestion}
                score={isScoreLoading ? null : score}
                handleDownload={handleDownload}
                setSuggestions={setSuggestions}
                loading={loading}
                errorMessage={error}
                setExpandedBulletPoint={setExpandedBulletPoint}
              />
            </div>
          )}
        </div>

        {isSidebarCollapsed && !isSidebarExpanded && (
          <div className="collapsed-sidebar" onClick={() => setIsSidebarExpanded(true)}>
            <div className="score-preview">
              {score && <div className="score">{score}</div>}
            </div>
          </div>
        )}

        {isSidebarExpanded && (
          <div className="expanded-sidebar">
            <div className="suggestions-container">
              <button
                className="close-button"
                onClick={() => setIsSidebarExpanded(false)}
              >
                <span>×</span>
              </button>
              <Suggestions
                suggestions={suggestions}
                expandedIndex={expandedIndex}
                handleToggleDropdown={handleToggleDropdown}
                isHovered={isHovered}
                handleTailorButtonClick={handleCoverLetterClick}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                handleSingleSuggestion={handleAcceptSingleSuggestion}
                score={isScoreLoading ? null : score}
                handleDownload={handleDownload}
                setSuggestions={setSuggestions}
                loading={loading}
                errorMessage={error}
                isInExpandedSidebar={true}
                setExpandedBulletPoint={setExpandedBulletPoint}
              />
            </div>
          </div>
        )}
      </>
    </AIProvider>
  );
};

export default ResumeComponent;
